import React, { useState, useEffect, ChangeEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import styles from "./EditOrganizationPage.module.scss";
import { Trash, Camera } from "@phosphor-icons/react";
import { useAppContext } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import TabbedFormLayout from "../../components/TabbedFormLayout/TabbedFormLayout";
import CustomInput from "../../components/Inputs/CustomInput";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CustomCalendar from "../../components/CustomCalendar/CustomCalendar";
import Modal from "../../components/Modal/Modal";
import { AxiosResponse, AxiosErrorType } from "../../types";

interface Organization {
  id: string;
  name: string;
  adminUserId: string;
  primaryPaymentAccountId: string;
}

interface OrganizationInfo {
  full_business_name: string;
  business_dba: string;
  business_address: string;
  main_business_phone: string;
  fax_number: string;
  support_email: string;
  business_type: string;
  business_industry: string;
  website_url: string;
  established_date: string;
  tax_id_number: string;
  registration_number: string;
  operating_hours: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  business_logo_url: string;
}

interface User {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  systemrole: string;
  roleid: number | null;
  apps: { app_id: string; access_level: string }[] | string[];
}

interface App {
  id: string;
  name: string;
}

interface Role {
  id: number;
  rolename: string;
  organizationid: string;
  permissions: Record<string, boolean>;
}

interface AccessLevel {
  id: number;
  name: string;
}

interface AuthContext {
  user: { id: string; issuperadmin: boolean };
}

interface AppContext {
  setEditOrganizationName: (name: string) => void;
}

export default function EditOrganizationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setEditOrganizationName } = useAppContext() as AppContext;
  const { user } = useAuth() as AuthContext;
  const [orgData, setOrgData] = useState<Organization>({
    id: "",
    name: "",
    adminUserId: "",
    primaryPaymentAccountId: "",
  });
  const [orgInfo, setOrgInfo] = useState<OrganizationInfo>({
    full_business_name: "",
    business_dba: "",
    business_address: "",
    main_business_phone: "",
    fax_number: "",
    support_email: "",
    business_type: "",
    business_industry: "",
    website_url: "",
    established_date: "",
    tax_id_number: "",
    registration_number: "",
    operating_hours: { monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: "" },
    business_logo_url: "",
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoFileName, setLogoFileName] = useState<string>("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState<boolean>(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [accessLevels, setAccessLevels] = useState<AccessLevel[]>([]);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newMemberFirstName, setNewMemberFirstName] = useState<string>("");
  const [newMemberLastName, setNewMemberLastName] = useState<string>("");
  const [newMemberEmail, setNewMemberEmail] = useState<string>("");
  const [newMemberPassword, setNewMemberPassword] = useState<string>("");
  const [newMemberRole, setNewMemberRole] = useState<string>("member");
  const [modalMessage, setModalMessage] = useState<string>("");
  const [modalIsError, setModalIsError] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string[]>([]);

  // Fetch organization data, logo, and access levels
  const fetchOrg = async () => {
    try {
      setError([]);
      console.log("[EditOrganizationPage] Fetching organization data for ID:", id);
      const [orgRes, infoRes, usersRes, appsRes, rolesRes, logoRes, accessLevelsRes] = await Promise.all([
        api.post<AxiosResponse<Organization>>("/core/organization/get", { organizationId: id }).catch((err: AxiosErrorType) => {
          console.error("[fetchOrg] Failed to fetch organization:", err.message, err.response?.data);
          setError(prev => [...new Set([...prev, `Failed to fetch organization: ${err.message}`])]);
          return {
            data: { id: "", name: "", adminUserId: "", primaryPaymentAccountId: "" },
            status: 200,
            statusText: "OK",
            headers: {},
            config: { headers: {} }
          } as AxiosResponse<Organization>;
        }),
        api.post<AxiosResponse<OrganizationInfo>>("/core/organization/organization_info/get", { organizationid: id }).catch((err: AxiosErrorType) => {
          console.error("[fetchOrg] Failed to fetch organization info:", err.message, err.response?.data);
          setError(prev => [...new Set([...prev, `Failed to fetch organization info: ${err.message}`])]);
          return {
            data: {
              full_business_name: "",
              business_dba: "",
              business_address: "",
              main_business_phone: "",
              fax_number: "",
              support_email: "",
              business_type: "",
              business_industry: "",
              website_url: "",
              established_date: "",
              tax_id_number: "",
              registration_number: "",
              operating_hours: { monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: "" },
              business_logo_url: ""
            },
            status: 200,
            statusText: "OK",
            headers: {},
            config: { headers: {} }
          } as AxiosResponse<OrganizationInfo>;
        }),
        api.post<AxiosResponse<{ users: User[] }>>("/core/team/get", { organizationid: id }).catch((err: AxiosErrorType) => {
          console.error("[fetchOrg] Failed to fetch users:", err.message, err.response?.data);
          setError(prev => [...new Set([...prev, `Failed to fetch users: ${err.message}`])]);
          return {
            data: { users: [] as User[] },
            status: 200,
            statusText: "OK",
            headers: {},
            config: { headers: {} }
          } as AxiosResponse<{ users: User[] }>;
        }),
        api.post<AxiosResponse<App[]>>("/core/organization/app/list", {}).catch((err: AxiosErrorType) => {
          console.error("[fetchOrg] Failed to fetch apps:", err.message, err.response?.data);
          setError(prev => [...new Set([...prev, `Failed to fetch apps: ${err.message}`])]);
          return {
            data: [] as App[],
            status: 200,
            statusText: "OK",
            headers: {},
            config: { headers: {} }
          } as AxiosResponse<App[]>;
        }),
        api.post<AxiosResponse<Role[]>>("/core/organization/orgroles", { organizationId: id }).catch((err: AxiosErrorType) => {
          console.error("[fetchOrg] Failed to fetch roles:", err.message, err.response?.data);
          setError(prev => [...new Set([...prev, `Failed to fetch roles: ${err.message}`])]);
          return {
            data: [] as Role[],
            status: 200,
            statusText: "OK",
            headers: {},
            config: { headers: {} }
          } as AxiosResponse<Role[]>;
        }),
        api.post<AxiosResponse<{ file_path: string | null, signed_url: string | null }>>("/core/organization/get-logo", { organization_id: id }).catch((err: AxiosErrorType) => {
          console.error("[fetchOrg] Failed to fetch logo:", err.message, err.response?.data);
          return { data: { file_path: null, signed_url: null } } as AxiosResponse<{ file_path: string | null, signed_url: string | null }>;
        }),
        api.get<AxiosResponse<{ accessLevels: AccessLevel[] }>>("/core/users/get-access-levels").catch((err: AxiosErrorType) => {
          console.error("[fetchOrg] Failed to fetch access levels:", err.message, err.response?.data);
          setError(prev => [...new Set([...prev, `Failed to fetch access levels: ${err.message}`])]);
          return {
            data: { accessLevels: [] as AccessLevel[] },
            status: 200,
            statusText: "OK",
            headers: {},
            config: { headers: {} }
          } as AxiosResponse<{ accessLevels: AccessLevel[] }>;
        })
      ]);

      console.log("[fetchOrg] Organization response:", orgRes.data);
      console.log("[fetchOrg] Organization info response:", infoRes.data);
      console.log("[fetchOrg] Users response:", usersRes.data);
      console.log("[fetchOrg] Apps response:", appsRes.data);
      console.log("[fetchOrg] Roles response:", rolesRes.data);
      console.log("[fetchOrg] Logo response:", logoRes.data);
      console.log("[fetchOrg] Access levels response:", accessLevelsRes.data);

      const org = orgRes as AxiosResponse<Organization>;
      const info = infoRes as AxiosResponse<OrganizationInfo>;
      const users = usersRes as AxiosResponse<{ users: User[] }>;
      const apps = appsRes as AxiosResponse<App[]>;
      const roles = rolesRes as AxiosResponse<Role[]>;
      const logo = logoRes as AxiosResponse<{ file_path: string | null, signed_url: string | null }>;
      const accessLevels = accessLevelsRes as AxiosResponse<{ accessLevels: AccessLevel[] }>;

      if (!org.data.id) throw new Error("Organization not found");

      setOrgData({
        id: org.data.id,
        name: org.data.name || "",
        adminUserId: org.data.adminUserId || "",
        primaryPaymentAccountId: org.data.primaryPaymentAccountId || ""
      });
      setOrgInfo({
        full_business_name: info.data.full_business_name || "",
        business_dba: info.data.business_dba || "",
        business_address: info.data.business_address || "",
        main_business_phone: info.data.main_business_phone || "",
        fax_number: info.data.fax_number || "",
        support_email: info.data.support_email || "",
        business_type: info.data.business_type || "",
        business_industry: info.data.business_industry || "",
        website_url: info.data.website_url || "",
        established_date: info.data.established_date || "",
        tax_id_number: info.data.tax_id_number || "",
        registration_number: info.data.registration_number || "",
        operating_hours: info.data.operating_hours || {
          monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: ""
        },
        business_logo_url: info.data.business_logo_url || ""
      });
      setTeamMembers(Array.isArray(users.data.users) ? users.data.users.map((user: User) => ({
        ...user,
        apps: Array.isArray(user.apps) ? user.apps.map((app: any) => {
          if (typeof app === 'string') {
            const [app_id, access_level] = app.split(':');
            return { app_id, access_level: access_level.toLowerCase() };
          }
          return { app_id: app.app_id, access_level: app.access_level.toLowerCase() };
        }) : []
      })) : []);
      setApps(Array.isArray(apps.data) ? apps.data : []);
      setRoles(Array.isArray(roles.data) ? roles.data : []);
      setAccessLevels(Array.isArray(accessLevels.data.accessLevels) ? accessLevels.data.accessLevels : []);
      setEditOrganizationName(org.data.name || "");
      if (logo.data.signed_url) {
        setLogoPreviewUrl(logo.data.signed_url);
      } else {
        setLogoPreviewUrl(null);
      }
      console.log("[EditOrganizationPage] Team Members set:", teamMembers);
      console.log("[EditOrganizationPage] Roles set:", roles);
      console.log("[EditOrganizationPage] Apps set:", apps);
      console.log("[EditOrganizationPage] Access Levels set:", accessLevels);
      console.log("[EditOrganizationPage] Logo Response:", logo.data);
    } catch (err: AxiosErrorType) {
      const errorMessage = err.message || "Failed to load organization data. Some features may be unavailable.";
      console.error("[EditOrganizationPage] Failed to fetch organization:", err.message, err.response?.data);
      setError(prev => [...new Set([...prev, errorMessage])]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      fetchOrg();
    }
    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleOrgSubmit = async () => {
    try {
      const formData = new FormData();
      if (logoFile) formData.append("org_logo", logoFile);
      formData.append("organization_id", id!);
      await api.post("/core/organization/update", {
        organizationId: id,
        updates: orgData,
        infoUpdates: orgInfo
      });
      if (logoFile) {
        const uploadRes = await api.post("/core/organization/upload-logo", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        console.log("[handleLogoChange] Upload response:", uploadRes.data);
        if (uploadRes.data?.file?.signed_url) {
          setLogoPreviewUrl(uploadRes.data.file.signed_url);
          setOrgInfo(prev => ({ ...prev, business_logo_url: uploadRes.data.file_path }));
        } else {
          await fetchOrg();
        }
      }
      setEditOrganizationName(orgData.name);
      navigate("/organizations");
    } catch (err: AxiosErrorType) {
      const errorMessage = err.message || "Failed to update organization";
      console.error("[handleOrgSubmit] Update failed:", err.message, err.response?.data);
      setError(prev => [...new Set([...prev, `Failed to update organization: ${errorMessage}`])]);
    }
  };

  const handleAddUser = async () => {
    if (!newMemberFirstName || !newMemberLastName || !newMemberEmail || !newMemberPassword) {
      setModalMessage("Please fill in all required fields: First Name, Last Name, Email, and Password.");
      setModalIsError(true);
      setShowModal(true);
      return;
    }

    try {
      console.log("[handleAddUser] Adding team member:", { newMemberFirstName, newMemberLastName, newMemberEmail, organizationid: id, systemrole: newMemberRole });
      const response = await api.post("/core/team/add", {
        firstname: newMemberFirstName,
        lastname: newMemberLastName,
        email: newMemberEmail,
        password: newMemberPassword,
        organizationid: id,
        systemrole: newMemberRole.startsWith("role_") ? "custom" : newMemberRole,
        roleid: newMemberRole.startsWith("role_") ? parseInt(newMemberRole.replace("role_", "")) : null
      });
      console.log("[handleAddUser] Team member added:", response.data);
      setShowAddModal(false);
      setNewMemberFirstName("");
      setNewMemberLastName("");
      setNewMemberEmail("");
      setNewMemberPassword("");
      setNewMemberRole("member");
      setModalMessage("Team member added successfully.");
      setModalIsError(false);
      setShowModal(true);
      fetchOrg();
    } catch (err: AxiosErrorType) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to add team member";
      console.error("[handleAddUser] Failed to add team member:", err.message, err.response?.data);
      setModalMessage(errorMessage);
      setModalIsError(true);
      setShowModal(true);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await api.post("/core/team/remove", {
        userid: userId,
        organizationid: id
      });
      console.log("[handleRemoveUser] Team member removed");
      setModalMessage("Team member removed successfully.");
      setModalIsError(false);
      setShowModal(true);
      fetchOrg();
    } catch (err: AxiosErrorType) {
      const errorMessage = err.message || "Failed to remove user";
      console.error("[handleRemoveUser] Failed to remove user:", err.message, err.response?.data);
      setModalMessage(errorMessage);
      setModalIsError(true);
      setShowModal(true);
    }
  };

  const handleUpdateRole = async (userId: string, systemrole: string, roleid: number | null) => {
    try {
      await api.post("/core/team/update", {
        userid: userId,
        organizationid: id,
        systemrole,
        roleid
      });
      console.log("[handleUpdateRole] Role updated");
      setModalMessage("Role updated successfully.");
      setModalIsError(false);
      setShowModal(true);
      fetchOrg();
    } catch (err: AxiosErrorType) {
      const errorMessage = err.message || "Failed to update role";
      console.error("[handleUpdateRole] Failed to update role:", err.message, err.response?.data);
      setModalMessage(errorMessage);
      setModalIsError(true);
      setShowModal(true);
    }
  };

  const handleAppAccess = async (userId: string, appId: string, action: "grant" | "revoke", accessLevel: string) => {
    try {
      console.log("[handleAppAccess] App access payload:", { organizationId: id, appAccess: { userId, appId, action, accessLevel } });
      const accessLevelRecord = accessLevels.find(level => level.name.toLowerCase() === accessLevel.toLowerCase());
      if (action === "grant" && !accessLevelRecord) {
        setModalMessage(`Invalid access level: ${accessLevel}. Please ensure access levels are configured.`);
        setModalIsError(true);
        setShowModal(true);
        return;
      }
      const response = await api.post("/core/organization/update", {
        organizationId: id,
        appAccess: { userId, appId, action, accessLevel }
      });
      console.log("[handleAppAccess] App access response:", response.data);
      setModalMessage(`App access ${action === "grant" ? "granted" : "revoked"} successfully.`);
      setModalIsError(false);
      setShowModal(true);
      fetchOrg();
    } catch (err: AxiosErrorType) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to manage app access";
      console.error("[handleAppAccess] Failed to manage app access:", err.message, err.response?.data);
      setModalMessage(errorMessage);
      setModalIsError(true);
      setShowModal(true);
    }
  };

  const handleSwitchActiveOrg = async (userId: string, organizationId: string) => {
    try {
      console.log("[handleSwitchActiveOrg] Attempting to switch active organization:", { userId, organizationId });
      await api.post("/core/users/switch-active-org", { userId, organizationId });
      console.log("[handleSwitchActiveOrg] Active organization switched");
      setModalMessage("Active organization switched successfully.");
      setModalIsError(false);
      setShowModal(true);
      fetchOrg();
    } catch (err: AxiosErrorType) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to switch active organization";
      console.error("[handleSwitchActiveOrg] Failed to switch active organization:", err.message, err.response?.data);
      setModalMessage(errorMessage);
      setModalIsError(true);
      setShowModal(true);
    }
  };

  const handleLogoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    console.log("[handleLogoChange] File input changed, selected file:", selected?.name);
    if (!selected) {
      setLogoError("No file selected");
      console.error("[handleLogoChange] No file selected");
      return;
    }

    if (!id) {
      setLogoError("Organization ID is missing");
      console.error("[handleLogoChange] Missing organization ID:", { id });
      return;
    }

    setLogoFile(selected);
    setLogoFileName(selected.name);
    setLogoUploading(true);
    setLogoError(null);

    const tempUrl = URL.createObjectURL(selected);
    setLogoPreviewUrl(tempUrl);

    const formData = new FormData();
    formData.append("org_logo", selected);
    formData.append("organization_id", id);
    console.log("[handleLogoChange] FormData contents:", {
      org_logo: selected.name,
      organization_id: id
    });

    try {
      const uploadRes = await api.post("/core/organization/upload-logo", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      console.log("[handleLogoChange] Upload response:", uploadRes.data);
      if (uploadRes.data?.file?.signed_url) {
        setLogoPreviewUrl(uploadRes.data.file.signed_url);
        setOrgInfo(prev => ({ ...prev, business_logo_url: uploadRes.data.file_path }));
      } else {
        await fetchOrg();
      }
    } catch (err: AxiosErrorType) {
      console.error("[handleLogoChange] Logo upload failed:", err.message, err.response?.data);
      setLogoError("Failed to upload logo: " + (err.response?.data?.message || err.message));
      await fetchOrg();
    } finally {
      setLogoUploading(false);
      URL.revokeObjectURL(tempUrl);
    }
  };

  const handleLogoClick = () => {
    console.log("[handleLogoClick] Logo overlay clicked");
    const input = document.getElementById("logo-upload") as HTMLInputElement;
    if (input) {
      console.log("[handleLogoClick] Triggering file input click");
      input.click();
    } else {
      console.error("[handleLogoClick] File input not found");
      setLogoError("File input not found");
    }
  };

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (error.length > 0 && !orgData.name) return (
    <div className={styles.error}>
      {error.map((err, index) => (
        <div key={index}>{err}</div>
      ))}
    </div>
  );

  const roleOptions = [
    { value: "member", label: "Member" },
    { value: "admin", label: "Admin" },
    ...(Array.isArray(roles) ? roles.map((role: Role) => ({ value: `role_${role.id}`, label: role.rolename })) : [])
  ];

  const appAccessOptions = [
    { value: "", label: "No Access" },
    ...(accessLevels.length > 0
      ? accessLevels.map(level => ({ value: level.name.toLowerCase(), label: level.name }))
      : [
          { value: "user", label: "User" },
          { value: "admin", label: "Admin" }
        ])
  ];

  const fieldTypeMap: Record<string, "text" | "email" | "tel" | "ein"> = {
    main_business_phone: "tel",
    support_email: "email",
    tax_id_number: "ein",
    registration_number: "text",
    full_business_name: "text",
    business_dba: "text",
    business_address: "text",
    business_type: "text",
    business_industry: "text",
    website_url: "text",
    fax_number: "tel"
  };

  const tabs = [
    {
      label: "Details",
      content: (
        <div className={styles.section}>
          <h3>Organization Details</h3>
          {error.length > 0 && error.some(err => err.includes("organization info")) && (
            <div className={styles.error}>Warning: Organization details may be incomplete due to data fetch issues.</div>
          )}
          <div className={styles.detailsContainer}>
            <div className={styles.logoContainer}>
              <div className={styles.logoWrapper}>
                <img
                  src={logoPreviewUrl || "../../src/assets/org-logo-placeholder.png"}
                  alt="Company Logo"
                  className={`${styles.logoPreview} ${logoUploading ? styles.uploading : ""}`}
                />
                <label htmlFor="logo-upload" className={styles.logoOverlay} onClick={handleLogoClick}>
                  <Camera size={32} color="#fff" />
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className={styles.hiddenFileInput}
                  id="logo-upload"
                  disabled={logoUploading}
                />
              </div>
              {logoFileName && <span className={styles.logoFileName}>{logoFileName}</span>}
              {logoError && <span className={styles.logoError}>{logoError}</span>}
            </div>
            <div className={styles.formGrid}>
              {/* Core Organization Fields */}
              <div className={styles.formGroup}>
                <h4>Core Information</h4>
                <CustomInput
                  label="Name"
                  value={orgData.name}
                  onChange={(val: string) => setOrgData({ ...orgData, name: val })}
                  required
                  type="text"
                  size="full"
                />
                <CustomInput
                  label="Admin User ID"
                  value={orgData.adminUserId}
                  onChange={(val: string) => setOrgData({ ...orgData, adminUserId: val })}
                  disabled={!user.issuperadmin}
                  type="text"
                  size="full"
                />
                <CustomInput
                  label="Primary Payment Account ID"
                  value={orgData.primaryPaymentAccountId}
                  onChange={(val: string) => setOrgData({ ...orgData, primaryPaymentAccountId: val })}
                  disabled={!user.issuperadmin}
                  type="text"
                  size="full"
                />
              </div>
              {/* Business Info */}
              <div className={styles.formGroup}>
                <h4>Business Information</h4>
                <CustomInput
                  label="Full Business Name"
                  value={orgInfo.full_business_name}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, full_business_name: val })}
                  required
                  type="text"
                  size="full"
                />
                <CustomInput
                  label="Business DBA"
                  value={orgInfo.business_dba}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, business_dba: val })}
                  type="text"
                  size="full"
                />
                <CustomInput
                  label="Business Address"
                  value={orgInfo.business_address}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, business_address: val })}
                  required
                  type="text"
                  size="full"
                />
                <CustomInput
                  label="Website URL"
                  value={orgInfo.website_url}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, website_url: val })}
                  type="text"
                  size="full"
                />
              </div>
              {/* Contact Info */}
              <div className={styles.formGroup}>
                <h4>Contact Information</h4>
                <CustomInput
                  label="Main Business Phone"
                  value={orgInfo.main_business_phone}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, main_business_phone: val })}
                  type="tel"
                  size="full"
                />
                <CustomInput
                  label="Fax Number"
                  value={orgInfo.fax_number}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, fax_number: val })}
                  type="tel"
                  size="full"
                />
                <CustomInput
                  label="Support Email"
                  value={orgInfo.support_email}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, support_email: val })}
                  type="email"
                  size="full"
                />
              </div>
              {/* Legal Info */}
              <div className={styles.formGroup}>
                <h4>Legal Information</h4>
                <CustomCalendar
                  label="Established Date"
                  value={orgInfo.established_date}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, established_date: val })}
                  required
                  size="full"
                />
                <CustomInput
                  label="Tax ID Number"
                  value={orgInfo.tax_id_number}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, tax_id_number: val })}
                  type="ein"
                  size="full"
                />
                <CustomInput
                  label="Registration Number"
                  value={orgInfo.registration_number}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, registration_number: val })}
                  type="text"
                  size="full"
                />
                <CustomInput
                  label="Business Type"
                  value={orgInfo.business_type}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, business_type: val })}
                  type="text"
                  size="full"
                />
                <CustomInput
                  label="Business Industry"
                  value={orgInfo.business_industry}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, business_industry: val })}
                  type="text"
                  size="full"
                />
              </div>
              {/* Operating Hours */}
              <div className={styles.fullRow}>
                <h4>Operating Hours</h4>
                <div className={styles.operatingHoursGrid}>
                  {Object.keys(orgInfo.operating_hours).map((day) => (
                    <div key={day} className={styles.formGroup}>
                      <CustomInput
                        label={day.charAt(0).toUpperCase() + day.slice(1)}
                        value={orgInfo.operating_hours[day as keyof OrganizationInfo['operating_hours']]}
                        onChange={(val: string) =>
                          setOrgInfo({
                            ...orgInfo,
                            operating_hours: { ...orgInfo.operating_hours, [day]: val }
                          })
                        }
                        placeholder="e.g., 9:00 AM - 5:00 PM"
                        type="text"
                        size="full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      label: "Team Members",
      content: (
        <div className={styles.section}>
          <h3>Team Members</h3>
          <p className={styles.note}>Add team members to the organization. App-specific user limits (e.g., ZoiBill) are enforced by the respective app.</p>
          {error.length > 0 && error.some(err => err.includes("roles")) && (
            <div className={styles.error}>Warning: Custom roles may be unavailable due to data fetch issues.</div>
          )}
          <div className={styles.addUser}>
            <button onClick={() => setShowAddModal(true)} className={styles.actionButton}>Add Member</button>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(teamMembers) && teamMembers.length > 0 ? (
                teamMembers.map((member: User) => {
                  const dropdownValue = member.roleid && Array.isArray(roles) && roles.find(r => r.id === member.roleid)
                    ? `role_${member.roleid}`
                    : member.systemrole?.toLowerCase() === "superadmin"
                      ? "superadmin"
                      : member.systemrole || "";
                  return (
                    <tr key={member.id}>
                      <td>{`${member.firstname} ${member.lastname}`}</td>
                      <td>{member.email}</td>
                      <td>
                        <CustomSelect
                          label="Role"
                          value={dropdownValue}
                          onChange={(val: string) => handleUpdateRole(
                            member.id,
                            val.startsWith("role_") ? "custom" : val,
                            val.startsWith("role_") ? parseInt(val.replace("role_", "")) : null
                          )}
                          options={roleOptions}
                          type="normal"
                          size="full"
                        />
                      </td>
                      <td className={styles.actions}>
                        <button onClick={() => handleRemoveUser(member.id)} disabled={member.id === orgData.adminUserId}>
                          <Trash size={20} />
                        </button>
                        {(user.issuperadmin || user.id === member.id) && (
                          <button onClick={() => handleSwitchActiveOrg(member.id, id!)}>
                            Set as Active
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4}>No team members found.</td>
                </tr>
              )}
            </tbody>
          </table>
          {showAddModal && (
            <div className={styles.modalOverlay}>
              <div className={styles.modal}>
                <h2 className={styles.modalTitle}>Add Team Member</h2>
                <div className={styles.modalContent}>
                  <CustomInput
                    label="First Name"
                    value={newMemberFirstName}
                    onChange={(val: string) => setNewMemberFirstName(val)}
                    type="text"
                    placeholder="Enter first name"
                    size="full"
                    required
                  />
                  <CustomInput
                    label="Last Name"
                    value={newMemberLastName}
                    onChange={(val: string) => setNewMemberLastName(val)}
                    type="text"
                    placeholder="Enter last name"
                    size="full"
                    required
                  />
                  <CustomInput
                    label="Email"
                    value={newMemberEmail}
                    onChange={(val: string) => setNewMemberEmail(val)}
                    type="email"
                    placeholder="Enter email"
                    size="full"
                    required
                  />
                  <CustomInput
                    label="Password"
                    value={newMemberPassword}
                    onChange={(val: string) => setNewMemberPassword(val)}
                    type="password"
                    placeholder="Enter temporary password"
                    size="full"
                    required
                  />
                  <CustomSelect
                    label="Role"
                    value={newMemberRole}
                    onChange={(val: string) => setNewMemberRole(val)}
                    options={roleOptions}
                    type="normal"
                    size="full"
                  />
                </div>
                <div className={styles.modalActions}>
                  <button onClick={handleAddUser} className={styles.actionButton}>Add Member</button>
                  <button onClick={() => {
                    setShowAddModal(false);
                    setNewMemberFirstName("");
                    setNewMemberLastName("");
                    setNewMemberEmail("");
                    setNewMemberPassword("");
                    setNewMemberRole("member");
                  }} className={styles.actionButton}>Cancel</button>
                </div>
              </div>
            </div>
          )}
          <Modal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            message={modalMessage}
            isError={modalIsError}
          />
        </div>
      )
    },
    {
      label: "App Access",
      content: (
        <div className={styles.section}>
          <h3>App Access</h3>
          {error.length > 0 && error.some(err => err.includes("apps") || err.includes("access levels")) && (
            <div className={styles.error}>No apps or access levels available due to data fetch issues.</div>
          )}
          {Array.isArray(apps) && apps.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>App</th>
                  <th>Access Level</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(teamMembers) && teamMembers.length > 0 ? (
                  teamMembers.map((member: User) => (
                    apps.map((app: App) => {
                      const access = Array.isArray(member.apps)
                        ? member.apps.find((a: any) => (typeof a === 'string' ? a.split(':')[0] : a.app_id) === app.id)
                        : null;
                      const accessLevel = access
                        ? typeof access === 'string'
                          ? access.split(':')[1]
                          : access.access_level
                        : "";
                      return (
                        <tr key={`${member.id}-${app.id}`}>
                          <td>{`${member.firstname} ${member.lastname}`}</td>
                          <td>{app.name}</td>
                          <td>
                            <CustomSelect
                              label="Access Level"
                              value={accessLevel.toLowerCase()}
                              onChange={(val: string) => handleAppAccess(member.id, app.id, val ? "grant" : "revoke", val)}
                              options={appAccessOptions}
                              type="normal"
                              size="full"
                            />
                          </td>
                          <td className={styles.actions}>
                            {access && (
                              <button onClick={() => handleAppAccess(member.id, app.id, "revoke", "")}>
                                <Trash size={20} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>No team members found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div>No apps available to configure.</div>
          )}
        </div>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <div className={styles.title}>Edit Organization: {orgData.name}</div>
      {error.length > 0 && (
        <div className={styles.error}>
          {error.map((err, index) => (
            <div key={index}>{err}</div>
          ))}
        </div>
      )}
      <TabbedFormLayout
        tabs={tabs}
        onSave={handleOrgSubmit}
        onCancel={() => navigate("/organizations")}
        showActions={true}
      />
    </div>
  );
}
